import { ReactNode, useState, useEffect, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  width?: string;
}

interface BulkAction {
  label: string;
  icon?: ReactNode;
  onClick: (selectedIds: string[]) => void;
  variant?: 'primary' | 'danger' | 'outline';
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  // Bulk operations
  selectable?: boolean;
  bulkActions?: BulkAction[];
  getRowId?: (row: T) => string;
  // Default sort
  defaultSortKey?: string;
  defaultSortOrder?: 'asc' | 'desc';
}

export default function DataTable<T>({
  columns,
  data: rawData,
  loading,
  onRowClick,
  emptyMessage = 'データがありません',
  selectable = false,
  bulkActions = [],
  getRowId,
  defaultSortKey,
  defaultSortOrder = 'desc',
}: DataTableProps<T>) {
  // Ensure data is always an array
  const data: T[] = Array.isArray(rawData) ? rawData : [];
  
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSortKey);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);

  // Clear selection when data changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [rawData]);

  const getValue = (row: T, key: string): any => {
    const keys = key.split('.');
    let value: any = row;
    for (const k of keys) {
      value = value?.[k];
    }
    return value;
  };

  // ソート処理
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  // ソートされたデータ
  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aVal = getValue(a, sortKey);
      const bVal = getValue(b, sortKey);
      
      // null/undefined を後ろに
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // 数値比較
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // 日付比較
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const dateA = Date.parse(aVal);
        const dateB = Date.parse(bVal);
        if (!isNaN(dateA) && !isNaN(dateB)) {
          return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        }
      }
      
      // 文字列比較
      const strA = String(aVal).toLowerCase();
      const strB = String(bVal).toLowerCase();
      if (sortOrder === 'asc') {
        return strA.localeCompare(strB, 'ja');
      }
      return strB.localeCompare(strA, 'ja');
    });
  }, [data, sortKey, sortOrder]);

  const extractRowId = (row: T): string => {
    if (getRowId) return getRowId(row);
    return (row as any).id || 
           (row as any).purchase?.id || 
           (row as any).shipment?.id || 
           (row as any).settlement?.id || 
           (row as any).invoice?.id || 
           '';
  };

  const handleSelectAll = () => {
    if (selectedIds.size === sortedData.length) {
      setSelectedIds(new Set());
    } else {
      const allIds = sortedData.map(row => extractRowId(row)).filter(id => id);
      setSelectedIds(new Set(allIds));
    }
  };

  const handleSelectRow = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkAction = (action: BulkAction) => {
    if (selectedIds.size === 0) return;
    action.onClick(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const isAllSelected = sortedData.length > 0 && selectedIds.size === sortedData.length;
  const isPartialSelected = selectedIds.size > 0 && selectedIds.size < sortedData.length;

  return (
    <div>
      {/* Bulk Actions Bar */}
      {selectable && selectedIds.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 mb-2 rounded-lg" style={{ backgroundColor: 'var(--accent-bg)' }}>
          <span className="text-sm font-medium" style={{ color: 'var(--accent-color)' }}>
            {selectedIds.size}件選択中
          </span>
          {bulkActions.map((action, index) => (
            <button
              key={index}
              onClick={() => handleBulkAction(action)}
              className={`btn btn-sm ${action.variant === 'danger' ? 'btn-danger' : action.variant === 'primary' ? 'btn-primary' : 'btn-outline'}`}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isPartialSelected;
                    }}
                    onChange={handleSelectAll}
                    className="checkbox"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={String(col.key)}
                  style={{ width: col.width }}
                  className={col.sortable ? 'cursor-pointer select-none hover:bg-gray-50' : ''}
                  onClick={() => col.sortable && handleSort(String(col.key))}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && (
                      <span className={sortKey === col.key ? '' : 'opacity-30'}>
                        {sortKey === col.key && sortOrder === 'asc' ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="text-center py-8">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: 'var(--accent-color)', borderTopColor: 'transparent' }}
                    />
                    読み込み中...
                  </div>
                </td>
              </tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => {
                const rowId = extractRowId(row);
                const isSelected = selectedIds.has(rowId);
                return (
                  <tr
                    key={rowId || index}
                    className={`${onRowClick ? 'cursor-pointer' : ''} ${isSelected ? 'selected-row' : ''}`}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {selectable && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(rowId)}
                          className="checkbox"
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={String(col.key)}>
                        {col.render ? col.render(row) : getValue(row, String(col.key))}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

